import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import useGameDetails from './useGameDetails.js';
import { GetDeepComparedData } from 'utils/Query/index.js';


const DUMMY_ROUND_DATA = { // mimics round structure as returned by the smart contract
  epoch: null,
  bullAmount: null,
  bearAmount: null,
  rewardBaseCalAmount: null,
  rewardAmount: null,
  lockPrice: null,
  closePrice: null,
  startTimestamp: null,
  lockTimestamp: null,
  closeTimestamp: null,
  lockPriceTimestamp: null,
  closePriceTimestamp: null,
  closed: false,
  canceled: false
}

const QEURY_DEFAULTS_FOR_ROUNDS = {
  placeholderData: DUMMY_ROUND_DATA,
  refetchInterval: 5 * 1000,
  staleTime: 60 * 1000,
  cacheTime: 1 * 60 * 60 * 1000,
  structuralSharing: GetDeepComparedData
}



export function useNewestRoundID() {
  const gameDetails = useGameDetails();

  const { data: newestRoundID } = useQuery({
    queryKey: ['newestRoundID', gameDetails.contracts.prediction?._address, gameDetails.wantedChain.id],
    queryFn: FetchNewestRoundNumber,
    placeholderData: 0,
    refetchInterval: 2.5 * 1000,
    staleTime: 60 * 1000,
    cacheTime: 4 * 60 * 60 * 1000,
  });

  async function FetchNewestRoundNumber() {
    if (!gameDetails.contracts.prediction)
      return 0; // not fully initialized yet
    const roundID = Number(await gameDetails.contracts.prediction.methods.currentEpoch().call());
    return roundID;
  }

  return newestRoundID;
};



export function useNextRound() {
  const gameDetails = useGameDetails();
  const currentEpoch = useNewestRoundID();

  const { data: nextRound } = useQuery({
    queryKey: ['round', currentEpoch, gameDetails.contracts.prediction?._address, gameDetails.wantedChain.id],
    ...QEURY_DEFAULTS_FOR_ROUNDS,
    queryFn: FetchNextRound,
  });

  async function FetchNextRound() {
    if (!gameDetails.contracts.prediction || currentEpoch == 0)
      return DUMMY_ROUND_DATA; // not fully initialized yet
    const round = await gameDetails.contracts.prediction.methods.Rounds(currentEpoch).call();
    return round;
  }

  return nextRound;
};



export function useCurrentRound() {
  const gameDetails = useGameDetails();
  const currentEpoch = useNewestRoundID();

  const { data: currentRound } = useQuery({
    queryKey: ['round', currentEpoch - 1, gameDetails.contracts.prediction?._address, gameDetails.wantedChain.id],
    ...QEURY_DEFAULTS_FOR_ROUNDS,
    queryFn: FetchCurrentRound,
    refetchOnMount: RefetchIfNecessary,
  });

  async function FetchCurrentRound() {
    if (!gameDetails.contracts.prediction || currentEpoch == 0)
      return DUMMY_ROUND_DATA; // not fully initialized yet
    const round = await gameDetails.contracts.prediction.methods.Rounds(currentEpoch - 1).call();
    return round;
  }

  function RefetchIfNecessary(query) {
    if (query === undefined) throw new SyntaxError('missing required argument: query');
    const round = query.state.data;
    if (round?.lockPrice == 0) {
      // assume that RPC-supplied data is up to date and lockPrice is indeed zero
      const tooMuchTimePassedSinceRoundWasLocked = ((Number(round.lockTimestamp) + 15) * 1000 < Date.now())
      if (!tooMuchTimePassedSinceRoundWasLocked) {
        return 'always'; // refetches always, regardless of conditions
      }
    }
    return true; // refetches only if stale
  }

  return currentRound;
};



export function usePreviousRound() {
  const gameDetails = useGameDetails();
  const currentEpoch = useNewestRoundID();

  const { data: previousRound } = useQuery({
    queryKey: ['round', currentEpoch - 2, gameDetails.contracts.prediction?._address, gameDetails.wantedChain.id],
    ...QEURY_DEFAULTS_FOR_ROUNDS,
    queryFn: FetchPreviousRound,
    refetchOnMount: RefetchIfNecessary,
  });

  async function FetchPreviousRound() {
    if (!gameDetails.contracts.prediction || currentEpoch == 0)
      return DUMMY_ROUND_DATA; // not fully initialized yet
    //const hardcodedRoundID = 268688; //debug
    //const hardcodedRoundID = 274841; //debug
    //const round = await gameDetails.contracts.prediction.methods.Rounds(hardcodedRoundID).call();
    const round = await gameDetails.contracts.prediction.methods.Rounds(currentEpoch - 2).call();
    return round;
  }

  function RefetchIfNecessary(query) {
    if (query === undefined) throw new SyntaxError('missing required argument: query');
    const round = query.state.data;
    if (round?.lockPrice == 0 || round?.closePrice == 0) {
      // assume that RPC-supplied data is up to date and lockPrice is indeed zero
      const tooMuchTimePassedSinceRoundWasClosed = ((Number(round.closeTimestamp) + 15) * 1000 < Date.now())
      if (!tooMuchTimePassedSinceRoundWasClosed) {
        return 'always'; // refetches always, regardless of conditions
      }
    }
    return true; // refetches only if stale
  }

  return previousRound;
};



export function useHistoricRound(roundID) {
  if (roundID === undefined) throw new SyntaxError('missing required argument: roundID');
  if (isNaN(roundID))
    throw new TypeError('wrong argument type: roundID must be a number, number-convertable string or null');

  const gameDetails = useGameDetails();
  const currentEpoch = useNewestRoundID();

  const refetchInterval = useMemo(GetRefetchInterval
    , [currentEpoch, roundID])


  const { data: historicRound, refetch: ForceRefetch, isFetching } = useQuery({
    queryKey: ['round', roundID, gameDetails.contracts.prediction?._address, gameDetails.wantedChain.id],
    ...QEURY_DEFAULTS_FOR_ROUNDS,
    queryFn: FetchHistoricRound,
    refetchInterval: refetchInterval,
    staleTime: 10 * 60 * 1000,
    refetchOnMount: !!refetchInterval,
  });

  useEffect(() => {
    //console.log(`round# ${roundID} refetch interval: ${refetchInterval}; fetching: ${isFetching}`);
    if (!historicRound.epoch && !isFetching)
      ForceRefetch();
  }, [refetchInterval, historicRound.epoch, isFetching, ForceRefetch]);


  async function FetchHistoricRound() {
    //console.info(`fetching historic round ${roundID}`);
    if (!gameDetails.contracts.prediction || roundID === null || roundID == 0)
      return DUMMY_ROUND_DATA; // not fully initialized yet
    const round = await gameDetails.contracts.prediction.methods.Rounds(roundID).call();
    return round;
  }

  function GetRefetchInterval() {
    const lastEndedRoundID = currentEpoch - 2;
    const roundTooOld = (roundID < lastEndedRoundID);
    if (roundTooOld || !currentEpoch || !roundID)
      return 0; // no refetching
    else
      return 10 * 1000;
  }

  return historicRound;
};
